import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowUpRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import TrialCTA from "@/components/TrialCTA";
import Seo from "@/components/Seo";
import SectionReveal from "@/components/SectionReveal";
import { trackCta } from "@/lib/analytics";

export default function Memberships() {
  const [items, setItems] = useState([]);
  const [faqs, setFaqs] = useState([]);
  useEffect(() => {
    api.get("/memberships").then((r) => setItems(r.data));
    api.get("/faqs").then((r) => setFaqs(r.data));
  }, []);

  return (
    <div>
      <Seo
        title="Memberships — Kore Access £49 & Kore Coaching £149"
        description="Choose between Kore Access (£49/mo) — a friendly gym you'll actually enjoy — or Kore Coaching (£149/mo) with two coached sessions a week, classes and a 40+ community that supports you."
        path="/memberships"
      />
      <section className="container-kore pt-16 pb-8">
        <span className="eyebrow">Memberships</span>
        <h1 className="display-1 mt-4 text-kore-forest max-w-3xl">Choose your membership.</h1>
        <p className="mt-6 text-kore-ink/70 max-w-2xl font-light">Choose the level of support that fits your life today. Change anytime.</p>

        <div className="mt-8 grid md:grid-cols-2 gap-4 max-w-3xl">
          <div className="rounded-2xl border border-kore-forest bg-kore-light/25 p-5">
            <div className="text-[11px] uppercase tracking-[0.22em] text-kore-forest">KORE Coaching</div>
            <p className="mt-2 text-kore-ink/85 text-sm leading-relaxed">For people who want structure, coaching, accountability and consistency.</p>
            <Link
              to="/30-day-trial"
              data-testid="memberships-cta-coached"
              onClick={() => trackCta({ cta_name: "memberships_start_trial", cta_text: "Start your 30 Day Trial", destination_url: "/30-day-trial", cta_location: "body" })}
              className="mt-4 inline-flex btn-primary text-xs"
            >
              Start your 30 Day Trial <ArrowUpRight size={12} />
            </Link>
          </div>
          <div className="rounded-2xl border border-kore-gold/25 bg-white p-5">
            <div className="text-[11px] uppercase tracking-[0.22em] text-kore-gold">KORE Access</div>
            <p className="mt-2 text-kore-ink/85 text-sm leading-relaxed">For people who already know how to train and want a welcoming place to do it.</p>
            <Link
              to="/memberships/kore-access"
              data-testid="memberships-cta-access"
              onClick={() => trackCta({ cta_name: "memberships_view_access", cta_text: "View KORE Access", destination_url: "/memberships/kore-access", cta_location: "body" })}
              className="mt-4 inline-flex btn-ghost text-xs"
            >
              View KORE Access <ArrowUpRight size={12} />
            </Link>
          </div>
        </div>
      </section>

      <SectionReveal as="section" data-testid="memberships-visual" className="container-kore pt-4 pb-4">
        <div className="rounded-3xl overflow-hidden aspect-[21/9] shadow-soft">
          <img src="/gallery/hero-gym-wide.webp" alt="Inside Kore Wellbeing — training floor" loading="lazy" className="w-full h-full object-cover object-center" />
        </div>
      </SectionReveal>

      <TrialCTA variant="inline" />

      <section className="container-kore py-16 grid md:grid-cols-2 gap-8">
        {items.map((m) => (
          <div key={m.slug} data-testid={`membership-card-${m.slug}`} className={`rounded-3xl p-10 md:p-12 relative ${m.highlighted ? "bg-kore-forest text-kore-warm border-2 border-kore-light" : "bg-white text-kore-forest shadow-soft"}`}>
            {m.highlighted && <span className="absolute -top-3 left-10 bg-kore-light text-kore-forest text-[11px] tracking-[0.2em] uppercase px-4 py-1 rounded-full">Most Popular</span>}
            <div className={`text-xs uppercase tracking-[0.24em] ${m.highlighted ? "text-kore-light" : "text-kore-gold"}`}>{m.tagline}</div>
            <h3 className="font-display text-5xl mt-3 font-light">{m.name}</h3>
            <div className="mt-6 flex items-baseline gap-1">
              <span className="font-display text-6xl font-light">£{m.price_gbp.toFixed(0)}</span>
              <span className={`text-sm ${m.highlighted ? "text-kore-warm/60" : "text-kore-ink/50"}`}>/{m.interval}</span>
            </div>
            <ul className={`mt-8 space-y-3 text-sm ${m.highlighted ? "text-kore-warm/85" : "text-kore-ink/80"}`}>
              {m.features.map((f) => <li key={f} className="flex gap-3"><span className={m.highlighted ? "text-kore-light" : "text-kore-gold"}>—</span>{f}</li>)}
            </ul>
            <div className="mt-10 flex gap-3 flex-wrap">
              <a href={m.signup_url} target="_blank" rel="noreferrer" className={m.highlighted ? "btn-gold" : "btn-primary"}>Join now <ArrowUpRight size={14} /></a>
              <Link to={`/memberships/${m.slug}`} className={m.highlighted ? "btn-ghost !text-kore-warm !border-kore-warm/30" : "btn-ghost"}>Details</Link>
            </div>
          </div>
        ))}
      </section>

      {/* Casual — pay as you go */}
      <SectionReveal as="section" data-testid="memberships-casual" className="container-kore pb-4">
        <div className="rounded-3xl bg-kore-warm border border-kore-gold/25 p-8 md:p-12 grid md:grid-cols-12 gap-8 md:gap-12 items-center">
          <div className="md:col-span-5">
            <span className="eyebrow">Not ready to commit?</span>
            <h3 className="font-display text-4xl md:text-5xl text-kore-forest font-light mt-3 leading-tight">Drop in for the day.</h3>
            <p className="mt-4 text-kore-ink/70 text-sm leading-relaxed">Pay-as-you-go options for visitors, friends of members, and anyone who wants a taste of Kore before joining.</p>
          </div>
          <div className="md:col-span-7 grid sm:grid-cols-2 gap-6">
            <div data-testid="casual-day-pass" className="rounded-2xl bg-white border border-kore-ink/10 p-6 flex flex-col">
              <div className="text-[11px] uppercase tracking-[0.22em] text-kore-gold">Gym day pass</div>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="font-display text-5xl text-kore-forest font-light">£12</span>
                <span className="text-xs text-kore-ink/50">/day</span>
              </div>
              <p className="mt-3 text-sm text-kore-ink/70 leading-relaxed">Full access to the training floor and all equipment for one day. No booking needed during staffed hours.</p>
              <Link to="/contact" className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-kore-forest border-b border-kore-gold pb-1 self-start">Enquire <ArrowUpRight size={12} /></Link>
            </div>
            <div data-testid="casual-drop-in" className="rounded-2xl bg-white border border-kore-ink/10 p-6 flex flex-col">
              <div className="text-[11px] uppercase tracking-[0.22em] text-kore-gold">Class drop-in</div>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="font-display text-5xl text-kore-forest font-light">£12</span>
                <span className="text-xs text-kore-ink/50">/class</span>
              </div>
              <p className="mt-3 text-sm text-kore-ink/70 leading-relaxed">Single-session drop-in for any Kore class — Kombat, Blast, Zones or Ascent.</p>
              <Link to="/book" className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-kore-forest border-b border-kore-gold pb-1 self-start">Book a class <ArrowUpRight size={12} /></Link>
            </div>
          </div>
        </div>
      </SectionReveal>

      {/* COMPARISON TABLE */}
      <SectionReveal as="section" data-testid="memberships-compare" className="container-kore py-20 md:py-24">
        <div className="max-w-2xl mb-10">
          <span className="eyebrow">Compare</span>
          <h2 className="display-2 mt-4 text-kore-forest">Which one is right for me?</h2>
          <p className="mt-4 text-kore-ink/70 leading-relaxed">A quick side-by-side so you can pick with confidence. Talk to us any time if you'd like to change.</p>
        </div>

        <div className="overflow-x-auto rounded-3xl border border-kore-gold/20 bg-white">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="border-b border-kore-gold/20">
                <th className="text-left px-6 py-5 text-[11px] uppercase tracking-[0.22em] text-kore-gold w-1/3">Feature</th>
                <th className="text-left px-6 py-5">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-kore-gold">Kore Access</div>
                  <div className="mt-1 font-display text-2xl text-kore-forest font-light">£49<span className="text-sm text-kore-ink/50">/mo</span></div>
                </th>
                <th className="text-left px-6 py-5 bg-kore-light/25 relative">
                  <span className="absolute -top-3 left-6 bg-kore-light text-kore-forest text-[9px] tracking-[0.2em] uppercase px-2.5 py-0.5 rounded-full">Most Popular</span>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-kore-gold">Kore Coaching</div>
                  <div className="mt-1 font-display text-2xl text-kore-forest font-light">£149<span className="text-sm text-kore-ink/50">/mo</span></div>
                </th>
                <th className="text-left px-6 py-5">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-kore-gold">1-to-1 PT</div>
                  <div className="mt-1 font-display text-2xl text-kore-forest font-light">from £60<span className="text-sm text-kore-ink/50">/session</span></div>
                </th>
              </tr>
            </thead>
            <tbody className="text-kore-ink/80">
              {[
                ["Open gym access",              "Staffed hours",         "Staffed hours",         "During booked session"],
                ["Kore classes (Kombat, Blast, Zones, Ascent)", "Discounted drop-in", "Unlimited",         "—"],
                ["Small-group coached PT",       "—",                     "2 sessions weekly",     "—"],
                ["Fully bespoke 1-to-1 PT",      "—",                     "—",                     "1, 2 or 3 x per week"],
                ["Written weekly plan",          "—",                     "—",                     "Included"],
                ["Nutrition guide",              "Starter guide",         "Full nutrition support","Bespoke support"],
                ["Monthly check-in",             "—",                     "Yes",                   "Every 4 weeks"],
                ["Community & socials",          "Yes",                   "Yes · priority",        "Yes"],
                ["Contract",                     "Rolling monthly",       "Rolling monthly",       "Rolling monthly"],
                ["Best for",                     "Self-motivated trainers","Progress with structure","Fully bespoke, private focus"],
              ].map(([f, a, c, p], i) => (
                <tr key={i} className={`border-t border-kore-gold/10 ${i % 2 === 0 ? "" : "bg-kore-warm/40"}`}>
                  <td className="px-6 py-4 text-kore-forest font-medium">{f}</td>
                  <td className="px-6 py-4">{a}</td>
                  <td className="px-6 py-4 bg-kore-light/15 text-kore-forest">{c}</td>
                  <td className="px-6 py-4">{p}</td>
                </tr>
              ))}
              <tr className="border-t border-kore-gold/20">
                <td className="px-6 py-6"></td>
                <td className="px-6 py-6"><Link to="/memberships/kore-access" data-testid="compare-cta-access" className="btn-ghost text-xs">Details <ArrowUpRight size={12} /></Link></td>
                <td className="px-6 py-6 bg-kore-light/15"><Link to="/memberships/kore-coached" data-testid="compare-cta-coached" className="btn-primary text-xs">Choose <ArrowUpRight size={12} /></Link></td>
                <td className="px-6 py-6"><Link to="/personal-training" data-testid="compare-cta-pt" className="btn-ghost text-xs">Details <ArrowUpRight size={12} /></Link></td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="mt-6 text-xs text-kore-ink/55 max-w-2xl">Still unsure? A free consultation is the fastest way to decide. We'll listen to your goals and recommend the honest best fit — even if that's "not right now".</p>
      </SectionReveal>

      <SectionReveal as="section" className="container-kore py-20 md:py-28 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-4">
          <span className="eyebrow">Answered</span>
          <h2 className="display-2 mt-3 text-kore-forest">Frequently asked.</h2>
        </div>
        <div className="md:col-span-8">
          <Accordion type="single" collapsible className="border-t border-kore-gold/25">
            {faqs.map((f) => (
              <AccordionItem key={f.id} value={f.id} className="border-b border-kore-gold/25">
                <AccordionTrigger className="text-left font-display text-xl md:text-2xl py-6 text-kore-forest">{f.question}</AccordionTrigger>
                <AccordionContent className="text-kore-ink/75 pb-6 leading-relaxed max-w-2xl">{f.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </SectionReveal>
    </div>
  );
}
